/** 
 * An implementation of the player which runs on its own standalone page. Not intended to be consumed as a module.
 */

import MediasitePlayer, { pluginNames } from '../player.js';
import { getPlayFrom, getDuration, getPresentationId, getLockControls, QueryStringParamProvider } from './player-page/helpers.js';
import { isPresentationId } from '../util';
import './player-page.css';

// Initialize a new Player instance by passing a <video> element to the constructor:
const paramProvider = new QueryStringParamProvider(location.search);
const presentationId = getPresentationId(location.pathname);

const isAutoPlay = paramProvider.getParamFromOptions(['autostart', 'autoplay'])?.toLowerCase() === 'true';

const player = new MediasitePlayer(document.querySelector('.mediasite-standalone-player-container > video'), {
    presentationId,
    apiConfig: {
        AntiForgery: {
            Header: window.SiteData.AntiForgeryHeaderName,
            Token: window.SiteData.AntiForgeryToken
        },
        Path: {
            ApplicationRoot: window.SiteData.ApplicationRoot,
            ApplicationScriptsRoot: window.SiteData.ApplicationScriptsRoot,
            PlayerService: window.SiteData.PlayerService,
            FileServer: window.SiteData.FileServer,
            WebAppsApi: window.SiteData.WebAppsApi
        }
    },
    controls: paramProvider.getParam('controls')?.toLowerCase() !== 'false',
    autoplay: isAutoPlay,
    loop: paramProvider.getParam('loop')?.toLowerCase() === 'true',
    fill: true,
    fluid: false,
    playFrom: getPlayFrom(location.search),
    duration: getDuration(location.search),
    lockControls: getLockControls(location.search),

    overrides: {
        playcover: {
            visible: paramProvider.getParamFromOptions(['cover', 'playcover'])?.toLowerCase() !== 'false' && !isAutoPlay,
            showPlayButton: paramProvider.getParam('coverplaybutton')?.toLowerCase() !== 'false',
            showTitle: paramProvider.getParam('covertitle')?.toLowerCase() !== 'false'
        },
        ...pluginNames.reduce((all, pluginName) => {
            if (paramProvider.getParamCaseSensitive(pluginName)?.toLowerCase() === 'false') {
                all[pluginName] = { enabled: false };
            }
            return all;
        }, {})
    }
});

player.one('playcoverloaded', () => {
    if (!isPresentationId(presentationId)) {
        player.loader.loadPresentation().then(() => (player.playcover.visible = false));
    } else if (!player.playcover) {
        //MVP #661 Make sure we remove preloading class if the listener in playcover.js didn't fire
        let elapsed = 0;
        const interval = setInterval(() => {
            elapsed += 250;
            if (elapsed >= 30000) {
                clearInterval(interval);
            }
            if (!player.playcover) {
                return;
            }
            const poster = player.playcover.player.getChild('PosterImage');
            if (poster.hasClass('mediasite-player__play-cover--preloading')) {
                poster.removeClass('mediasite-player__play-cover--preloading');
                clearInterval(interval);
            }
        }, 250);
    }
});

window.MediasitePlayer = player;
